import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    ViewChild,
} from '@angular/core';
import { findStateForMetric, getColours } from '@dxp/core/helpers';
import { IMetric, MetricPaletteType } from '@dxp/core/models';
import { HeartbeatService } from '@dxp/core/services';
import { ChartConfiguration } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';

/**
 * Component to display doughnut chart with centered text.
 * The ring represents a percentage value with a colour code indicating the status.
 *
 * @selector doughnut
 * @standalone true
 * @component DoughnutComponent
 * @imports CommonModule, BaseChartDirective
 *
 * @Input metric - Object conforming to IMetric interface, representing the ring in the chart.
 * @Input content - The inner content
 *
 * Usage:
 * <telxl-doughnut [metric]="data" [content]="<span>100%</span>"></doughnut>
 *
 * Note:
 * A data item contains the percent value of the ring and state change values.
 * The state change value is an array of numbers to set the RAG status.
 *
 * e.g. { percent: 80, state: [{25, 'success'}, {75, 'danger'}]}
 * In this scenario a percent value over 25 will render success state and over 75 will render danger state.
 *
 * e.g. { percent: 30, state: [{50, 'warning'}]}
 * In this scenario a percent value over 50 will render warning state. No red state is configured.
 *
 */
@Component({
    selector: 'telxl-doughnut',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [BaseChartDirective],
    templateUrl: './doughnut.component.html',
    styleUrl: './doughnut.component.scss',
})
export class TelXLDoughnutComponent {
    readonly #heartbeatService = inject(HeartbeatService);

    defaultState = input<MetricPaletteType>('success');
    emptyState = input<MetricPaletteType>('dark');
    content = input('');
    metric = input.required<IMetric>();
    height = input.required<string>();
    cutout = input<string>('96%');

    @ViewChild(BaseChartDirective, { static: false })
    chart?: BaseChartDirective;

    readonly doughnutChartOptions = computed<
        ChartConfiguration<'doughnut'>['options']
    >(() => {
        return {
            cutout: this.cutout, // Use the dynamic cutout value
            responsive: true,
            animation: false,
            maintainAspectRatio: true,
            plugins: {
                tooltip: { enabled: false },
                legend: { display: false },
            },
        };
    });

    doughnutChartType: ChartConfiguration<'doughnut'>['type'] = 'doughnut';

    readonly doughnutChartData = computed<
        ChartConfiguration<'doughnut'>['data']
    >(() => {
        this.#heartbeatService.lastHeartbeat();

        const state = findStateForMetric(
            this.metric(),
            this.defaultState(),
            this.emptyState(),
        );

        const colours = getColours();
        const colour = colours[state as keyof typeof colours];

        return {
            datasets: [
                {
                    data: [this.metric().percent, 100 - this.metric().percent],
                    backgroundColor: [colour, colours['background']],
                    hoverBackgroundColor: [colour, colours['background']],
                    borderWidth: 0,
                    borderRadius: 20,
                },
            ],
        };
    });
}
